// WebSocket API response types from documentation
export const RESULT_TYPES = {
    CHAT_RESPONSE: 'CHAT_RESPONSE',
    NEEDS_REASONING: 'NEEDS_REASONING',
    ERROR: 'ERROR',
    AUTH_ERROR: 'auth_error',
    STATUS_COMMAND: 'STATUS_COMMAND',
    DATASET_SELECTION: 'DATASET_SELECTION',
    NEEDS_DATASET_CLARIFICATION: 'NEEDS_DATASET_CLARIFICATION',
}

// WebSocket action types
export const ACTION_TYPES = {
    EXPORT_EXCEL: 'exportExcel',
    EXPORT_CSV: 'exportCSV',
}

// Processing status types
export const MESSAGE_TYPES = {
    STATUS_UPDATE: 'status_update',
    PROCESSING_STATUS: 'processing_status',
    TIME_ESTIMATE: 'time_estimate',
}

// Create AI chat handler for WebSocket API responses
export const createAIChatHandler = ({
    onMessage,
    onError,
    onAuthError,
    onStatus,
    onNeedsReasoning,
    onExport,
}) => {
    return (message) => {
        if (!message) return

        // Handle export responses - detect by presence of base64 data + filename + mimeType
        // Export responses don't have an 'action' field, so we detect them by their structure
        const isExportResponse =
            message.data &&
            message.filename &&
            message.mimeType &&
            (message.mimeType.includes('spreadsheet') || message.mimeType.includes('csv'))

        if (isExportResponse) {
            handleExportResponse(message, onExport, onMessage)
            return
        }

        // Handle status updates during processing
        if (message.message?.text) {
            handleStatusUpdate(message.message.text, onStatus)
            return
        }

        // Handle final responses based on resultType or type
        // The resultType might be in the message directly or in the header
        const status = message.status
        const resultType = message.resultType || message.header?.resultType || message.type

        if (
            status === 'error' ||
            resultType === RESULT_TYPES.ERROR ||
            resultType === RESULT_TYPES.AUTH_ERROR
        ) {
            handleError(message, onError, onMessage, onAuthError)
            return
        }

        // Handle both 'complete' and 'success' status
        if (status === 'complete' || status === 'success') {
            switch (resultType) {
                case RESULT_TYPES.CHAT_RESPONSE:
                    // Check if it's actually a dataset clarification response
                    if (message.content?.datasetAlternatives) {
                        handleDatasetClarification(message, onMessage)
                    } else {
                        handleChatResponse(message, onMessage)
                    }
                    break

                case RESULT_TYPES.NEEDS_REASONING:
                    handleNeedsReasoning(message, onNeedsReasoning)
                    break

                case RESULT_TYPES.STATUS_COMMAND:
                    handleStatusCommand(message, onMessage)
                    break

                case RESULT_TYPES.DATASET_SELECTION:
                    handleDatasetSelection(message, onMessage)
                    break

                case RESULT_TYPES.NEEDS_DATASET_CLARIFICATION:
                    handleDatasetClarification(message, onMessage)
                    break

                case 'datasetDescription':
                    handleDatasetDescription(message, onMessage)
                    break

                default:
                    console.log('Unknown result type:', resultType, message)
            }
        } else if (status === 'processing') {
            // Immediate response after sending message
            handleProcessingStatus(message, onStatus)
        }
    }
}

// Individual handlers for WebSocket API responses

// Handle status updates during processing
const handleStatusUpdate = (statusText, onStatus) => {
    if (onStatus?.onProcessingStatus) {
        onStatus.onProcessingStatus({
            status: 'processing',
            message: statusText,
        })
    }
}

// Handle CHAT_RESPONSE - successful query result with structured format
const handleChatResponse = (data, onMessage) => {
    if (!onMessage) return

    // Extract the text content - it might be in content.text.response or result
    let textContent = ''
    if (data.content?.text?.response) {
        textContent = data.content.text.response
    } else if (data.result?.text) {
        textContent = data.result.text
    } else if (typeof data.result === 'string') {
        textContent = data.result
    }

    // Create a structured message object that preserves the API response structure
    const message = {
        messageID: Date.now(),
        role: 'scoop', // Scoop AI response, not a generic assistant
        timestamp: new Date().toISOString(),
        type: 'structured_response',

        // Preserve the structured response format
        header: data.header,
        content: data.content,
        result: data.result,
        footer: data.footer,
        metadata: data.metadata,

        // Use the extracted text content or format from data
        text: textContent || formatResponseAsText(data),

        // Store the raw response
        rawData: data,
    }

    onMessage(message)
}

// Format structured response as plain text for fallback display
const formatResponseAsText = (data) => {
    let text = ''

    // Header description
    if (data.header?.description) {
        text += data.header.description + '\n\n'
    }

    // Visualization info
    if (data.result?.visualization) {
        const viz = data.result.visualization
        text += `📊 ${viz.chartTitle || 'Chart'}: ${viz.type} visualization\n`

        // Add data preview if available
        if (viz.data?.columns && viz.data.columns.length > 0) {
            const [labels, ...dataSeries] = viz.data.columns
            if (labels && labels.length > 1) {
                text += `Showing ${labels.length - 1} data points\n`
            }
        }
        text += '\n'
    }

    // SQL query
    if (data.result?.query?.sql) {
        text += `**Query:** \`${data.result.query.sql.split('\n')[0].substring(0, 100)}...\`\n`
        if (data.result.query.executionTime) {
            text += `Execution time: ${data.result.query.executionTime}ms\n`
        }
        text += '\n'
    }

    // Findings (for reasoning responses)
    if (data.result?.findings?.length > 0) {
        text += '**Key Findings:**\n'
        data.result.findings.forEach((finding) => {
            text += `• ${finding.finding}`
            if (finding.confidence !== undefined) {
                const conf =
                    typeof finding.confidence === 'number'
                        ? `${(finding.confidence * 100).toFixed(0)}%`
                        : finding.confidence
                text += ` (${conf} confidence)`
            }
            text += '\n'
            if (finding.businessImpact) {
                text += `  Impact: ${finding.businessImpact}\n`
            }
            if (finding.supportingEvidence?.length > 0) {
                text += '  Supporting evidence:\n'
                finding.supportingEvidence.forEach((ev) => {
                    text += `    - ${ev}\n`
                })
            }
        })
        text += '\n'
    }

    // Recommendations (for reasoning responses)
    if (data.result?.recommendations?.length > 0) {
        text += '**Recommendations:**\n'
        data.result.recommendations.forEach((rec) => {
            text += `• ${rec.action || rec.recommendation}`
            if (rec.priority) text += ` [${rec.priority}]`
            text += '\n'
            if (rec.rationale) text += `  ${rec.rationale}\n`
        })
        text += '\n'
    }

    // Limitations (for reasoning responses)
    if (data.result?.limitations?.length > 0) {
        text += '**Analysis Limitations:**\n'
        data.result.limitations.forEach((limitation) => {
            text += `• ${limitation}\n`
        })
        text += '\n'
    }

    // Footer summary
    if (data.footer?.summary) {
        text += data.footer.summary + '\n\n'
    }

    // Follow-up prompts
    if (data.footer?.followUpPrompts?.length > 0) {
        text += '**Suggested questions:**\n'
        data.footer.followUpPrompts.forEach((prompt) => {
            text += `• ${prompt.prompt}\n`
        })
        text += '\n'
    }

    // Metadata
    if (data.metadata?.rowCount !== undefined) {
        text += `_${data.metadata.rowCount} rows returned_\n`
    }

    return text.trim() || 'Query completed successfully'
}

// Handle NEEDS_REASONING - deep analysis available
const handleNeedsReasoning = (data, onNeedsReasoning) => {
    if (onNeedsReasoning) {
        onNeedsReasoning({
            query: data.query,
            message: data.message || 'This question might benefit from deeper analysis',
        })
    }
}

// Handle ERROR responses
const handleError = (data, onError, onMessage, onAuthError) => {
    // Check if this is an authentication error
    const resultType = data.resultType || data.header?.resultType || data.type
    const errorCode = data.error?.code || data.code
    const isAuthError =
        resultType === RESULT_TYPES.AUTH_ERROR ||
        errorCode?.startsWith('token_') ||
        errorCode === 'invalid_token' ||
        errorCode === 'invalid_signature'

    // If it's an auth error and we have a handler, call it and don't show as chat message
    if (isAuthError && onAuthError) {
        onAuthError({
            code: errorCode || 'auth_error',
            message: data.error?.message || data.message || 'Authentication failed',
        })
        return
    }

    // Check if there's a user-friendly message to display
    const userMessage = data.userMessage
    const requiresRefresh = data.requiresRefresh === true
    const retryable = data.retryable !== false // Default to true
    const isUserFriendly = data.userFriendly !== false // Default to true for user visibility

    // Use userMessage if available, otherwise extract error from various locations
    const errorMessage =
        userMessage ||
        data.error ||
        data.message ||
        data.content?.error ||
        data.content?.message ||
        data.content?.text ||
        'Something went wrong processing your request. Please try rephrasing your question.'

    if (onError) {
        onError({
            message: errorMessage,
            userFriendly: isUserFriendly,
            requiresRefresh: requiresRefresh,
            retryable: retryable,
        })
    }

    // Always add as message so user sees the error
    if (onMessage) {
        onMessage({
            messageID: Date.now(),
            text: errorMessage.replaceAll('❌', ''),
            role: 'error',
            timestamp: new Date().toISOString(),
            isError: true,
            requiresRefresh: requiresRefresh,
            retryable: retryable,
            rawData: data, // Include full socket response for debugging/monitoring
        })
    }
}

// Handle STATUS_COMMAND - informational messages
const handleStatusCommand = (data, onMessage) => {
    if (onMessage && data.result?.message) {
        onMessage({
            messageID: Date.now(),
            text: data.result.message,
            role: 'system',
            timestamp: new Date().toISOString(),
        })
    }
}

// Handle DATASET_SELECTION - no dataset selected
const handleDatasetSelection = (data, onMessage) => {
    if (onMessage) {
        onMessage({
            messageID: Date.now(),
            text: data.message || 'Please select a dataset first',
            role: 'system',
            timestamp: new Date().toISOString(),
        })
    }
}

// Handle NEEDS_DATASET_CLARIFICATION - show available datasets
const handleDatasetClarification = (data, onMessage) => {
    if (!onMessage) return

    // Extract dataset alternatives from the response
    const content = data.content || {}
    const datasetAlternatives = content.datasetAlternatives || {}
    const alternatives = datasetAlternatives.alternatives || []

    // Create structured message with dataset alternatives for clickable selection
    const message = {
        messageID: Date.now(),
        text: 'I can help you analyze data from these datasets:',
        role: 'assistant',
        timestamp: new Date().toISOString(),
        datasetAlternatives: alternatives,
        recommendedDataset: datasetAlternatives.recommended,
        isDatasetList: true,
        needsDatasetSelection: true, // Flag to show clickable options
    }

    onMessage(message)
}

// Handle DATASET_DESCRIPTION - dataset description response
const handleDatasetDescription = (data, onMessage) => {
    if (!onMessage) return

    // Extract content from the response
    const content = data.content || {}
    const text = content.text || ''
    const datasetName = content.datasetName || 'Dataset'

    // Create a structured message for dataset description
    const message = {
        messageID: Date.now(),
        role: 'scoop',
        timestamp: new Date().toISOString(),
        type: 'dataset_description',

        // Store the markdown content for display
        text: text,

        // Store metadata in content structure for StructuredMessage component
        content: {
            text: {
                response: text,
                datasetName: datasetName,
                format: content.format || 'markdown',
                inboxID: content.inboxID,
            },
            // Include suggestions at the content level for follow-up prompts
            suggestions: content.suggestions || [],
        },

        // Store raw data for reference
        rawData: data,
    }

    onMessage(message)
}

// Handle immediate processing response
const handleProcessingStatus = (data, onStatus) => {
    if (onStatus?.onProcessingStatus) {
        onStatus.onProcessingStatus({
            status: 'processing',
            message: data.message || 'Request received, processing...',
        })
    }
}

// Handle export responses (Excel/CSV exports)
const handleExportResponse = (data, onExport, onMessage) => {
    // Handle successful export
    if (data.status === 'success' && data.data) {
        try {
            // Convert base64 to blob and trigger download
            const binaryData = atob(data.data)
            const bytes = new Uint8Array(binaryData.length)
            for (let i = 0; i < binaryData.length; i++) {
                bytes[i] = binaryData.charCodeAt(i)
            }

            const blob = new Blob([bytes], { type: data.mimeType })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = data.filename || 'export.xlsx'

            try {
                document.body.appendChild(a)
                a.click()
            } catch (error) {
                console.error('Download failed:', error)
                throw error
            } finally {
                // Cleanup with safety check
                if (a.parentNode === document.body) {
                    document.body.removeChild(a)
                }
                URL.revokeObjectURL(url)
            }

            // Call onExport callback if provided
            if (onExport) {
                onExport({
                    success: true,
                    filename: data.filename,
                    size: data.sizeReadable,
                    metadata: data.metadata,
                })
            }

            // Show success message to user
            if (onMessage) {
                const successText = data.metadata
                    ? `Downloaded ${data.filename} (${data.sizeReadable}, ${data.metadata.rows} rows)`
                    : `Downloaded ${data.filename}`

                onMessage({
                    messageID: Date.now(),
                    text: successText,
                    role: 'system',
                    timestamp: new Date().toISOString(),
                })
            }
        } catch (error) {
            // Call onExport callback with error
            if (onExport) {
                onExport({
                    success: false,
                    error: error.message,
                })
            }

            // Show error message to user
            if (onMessage) {
                onMessage({
                    messageID: Date.now(),
                    text: `Failed to download export: ${error.message}`,
                    role: 'error',
                    timestamp: new Date().toISOString(),
                })
            }
        }
    } else if (data.status === 'error') {
        // Call onExport callback with error
        if (onExport) {
            onExport({
                success: false,
                error: data.error || 'Unknown error',
            })
        }

        // Show error message to user
        if (onMessage) {
            onMessage({
                messageID: Date.now(),
                text: `Export failed: ${data.error || 'Unknown error'}`,
                role: 'error',
                timestamp: new Date().toISOString(),
                rawData: data, // Include full socket response for debugging/monitoring
            })
        }
    }
}

// Format request according to WebSocket API documentation
// Auth data (userID, workspaceID, token) will be injected by useWebSocket hook
export const formatChatRequest = (prompt, inboxID, chatID, options = {}) => {
    const request = {
        action: 'sendMessage',
        inboxID: inboxID,
        chatID: chatID,
        prompt: prompt.trim(),
        // Don't include userID, workspaceID, token here - they'll be added by useWebSocket
    }

    // Handle reasoning flags according to documentation:
    // - Default: checkReasoning = true (let system decide)
    // - Quick analysis after NEEDS_REASONING: checkReasoning = false, forceReasoning = false
    // - Deep analysis after NEEDS_REASONING: checkReasoning = false, forceReasoning = true

    if (options.forceReasoning === true) {
        // Deep analysis requested
        request.forceReasoning = true
        request.checkReasoning = false
    } else if (options.checkReasoning === false) {
        // Explicitly don't check (for quick analysis)
        request.checkReasoning = false
    } else if (options.checkReasoning !== undefined) {
        // Use provided value
        request.checkReasoning = options.checkReasoning
    } else {
        // Default: let system check if reasoning is needed
        request.checkReasoning = true
    }

    // Add other optional parameters
    if (options.themeID) request.themeID = options.themeID
    if (options.filter) request.filter = options.filter

    return request
}

// Helper to format chat context for AI if needed
export const formatChatContext = (messages) => {
    return messages.map((msg) => ({
        role: msg.role,
        content: msg.text,
        timestamp: msg.timestamp,
    }))
}
